import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { FileText, Pen, Check, AlertCircle, Download, Eraser } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../lib/authHeaders';

import { API } from '../config/api';

const NDASigningModal = ({ isOpen, onClose, template, onSigned }) => {
  const [step, setStep] = useState(1); // 1: Read, 2: Sign
  const [typedSignature, setTypedSignature] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasReadNDA, setHasReadNDA] = useState(false);
  
  // Canvas for signature
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  
  // Initialize canvas
  useEffect(() => {
    if (step === 2 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e3a5f';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [step]);
  
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };
  
  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
    setHasDrawn(true);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };
  
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };
  
  const getSignatureData = () => {
    if (!canvasRef.current) return '';
    return canvasRef.current.toDataURL('image/png');
  };
  
  const handleSubmit = async () => {
    if (!typedSignature.trim()) {
      toast.error('Please type your full legal name');
      return;
    }
    
    if (!hasDrawn) {
      toast.error('Please sign in the signature box');
      return;
    }
    
    if (!agreedToTerms) {
      toast.error('Please agree to the terms of the NDA');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/nda/sign-core-group`, {
        typed_signature: typedSignature.trim(),
        drawn_signature: getSignatureData(),
        agreed_to_terms: true
      }, {
        headers,
        withCredentials: true
      });
      
      toast.success('NDA signed successfully!');
      onSigned && onSigned();
      onClose();
    } catch (error) {
      console.error('Error signing NDA:', error);
      // Handle validation errors from FastAPI (detail can be string or array of objects)
      let errorMessage = 'Failed to sign NDA. Please try again.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as array of {type, loc, msg, input, url}
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleScroll = (e) => {
    const target = e.target;
    const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
    if (scrolledToBottom) {
      setHasReadNDA(true);
    }
  };
  
  if (!template) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-6 w-6 text-primary" />
            {template.title || 'Non-Disclosure Agreement'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 
              ? 'Please read the entire agreement carefully before signing.' 
              : 'Please provide your signature to complete the agreement.'}
          </DialogDescription>
        </DialogHeader>
        
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 py-2">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-white' : 'bg-muted'}`}>
              {step > 1 ? <Check className="h-5 w-5" /> : '1'}
            </div>
            <span className="text-sm font-medium">Read Agreement</span>
          </div>
          <div className="w-16 h-0.5 bg-muted" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-white' : 'bg-muted'}`}>
              2
            </div>
            <span className="text-sm font-medium">Sign</span>
          </div>
        </div>
        
        {step === 1 ? (
          /* Step 1: Read NDA */
          <div className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <AlertCircle className="h-4 w-4" />
                <span>Scroll to the bottom to enable the continue button</span>
              </div>
            </div>
            
            <ScrollArea className="h-[400px] border rounded-lg p-4 bg-white dark:bg-slate-900" onScrollCapture={handleScroll}>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {/* Company Header */}
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-primary mb-1">{template.company_name}</h2>
                  <p className="text-muted-foreground text-sm">{template.company_address}</p>
                </div>
                
                <h1 className="text-center text-xl font-bold mb-4">{template.title}</h1>
                
                {/* NDA Content */}
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {template.content}
                </div>
              </div>
            </ScrollArea>
            
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => setStep(2)}
                disabled={!hasReadNDA}
                className="min-w-[150px]"
              >
                {hasReadNDA ? 'Continue to Sign' : 'Read Agreement First'}
              </Button>
            </div>
          </div>
        ) : (
          /* Step 2: Sign NDA */
          <div className="space-y-6">
            {/* Typed Signature */}
            <div className="space-y-2">
              <Label htmlFor="typed-signature" className="text-base font-semibold">
                Type Your Full Legal Name
              </Label>
              <Input
                id="typed-signature"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                placeholder="Enter your full legal name"
                className="text-lg"
              />
            </div>
            
            {/* Drawn Signature */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Pen className="h-4 w-4" />
                  Draw Your Signature
                </Label>
                <Button variant="ghost" size="sm" onClick={clearSignature}>
                  <Eraser className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
              <div className="border-2 border-dashed rounded-lg p-2 bg-white">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Use your mouse or finger to draw your signature above
              </p>
            </div>
            
            {/* Agreement Checkbox */}
            <div className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30">
              <Checkbox
                id="agree-terms"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                className="mt-1"
              />
              <Label htmlFor="agree-terms" className="text-sm leading-relaxed cursor-pointer">
                I, <strong>{typedSignature || '[Your Name]'}</strong>, hereby acknowledge that I have read, 
                understood, and agree to be bound by all terms and conditions of this Non-Disclosure Agreement. 
                I understand that violation of this agreement may result in legal action.
              </Label>
            </div>
            
            {/* Actions */}
            <div className="flex justify-between gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back to Agreement
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !typedSignature.trim() || !hasDrawn || !agreedToTerms}
                className="min-w-[150px]"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Signing...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Sign Agreement
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NDASigningModal;
